import { useEffect } from 'react';
import './GlacialLanceAnimation.css';

/**
 * PROFESSIONAL GLACIAL LANCE ANIMATION
 * Ice-type physical move - summons icicle lances that strike the opponent
 * 
 * Key Features:
 * - Ice-type physical attack (120 base power, 100% accuracy)
 * - Calyrex-Ice Rider signature move
 * - Multiple icicle lances raining down from above
 * - Professional gaming quality with crystalline frost effects
 */
export default function GlacialLanceAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const elements = [];

    // PHASE 1: Frost Energy Gathering (0-500ms)
    playerEl.classList.add('glacial-lance-user-charge');

    const frostAura = document.createElement('div');
    frostAura.className = 'glacial-lance-frost-aura';
    frostAura.style.left = `${playerX - 80}px`;
    frostAura.style.top = `${playerY - 80}px`;
    container.appendChild(frostAura);
    elements.push(frostAura);

    // Frost crystal particles
    for (let i = 0; i < 12; i++) {
      const crystal = document.createElement('div');
      crystal.className = 'glacial-lance-crystal';
      const angle = (i / 12) * 360;
      const radius = 60 + (i % 3) * 12;
      crystal.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 5}px`;
      crystal.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 5}px`;
      crystal.style.animationDelay = `${i * 35}ms`;
      container.appendChild(crystal);
      elements.push(crystal);
    }

    // PHASE 2: Lance Formation Above Opponent (500-900ms)
    const phase2Timer = setTimeout(() => {
      // Icicle lances forming above opponent
      for (let i = 0; i < 5; i++) {
        const lanceTimer = setTimeout(() => {
          const lance = document.createElement('div');
          lance.className = 'glacial-lance-icicle';
          lance.style.left = `${opponentX + (i - 2) * 30 - 6}px`;
          lance.style.top = `${opponentY - 120 - Math.random() * 30}px`;
          lance.style.animationDelay = `${i * 40}ms`;
          container.appendChild(lance);
          elements.push(lance);

          // Frost mist around each lance
          const mist = document.createElement('div');
          mist.className = 'glacial-lance-mist';
          mist.style.left = `${opponentX + (i - 2) * 30 - 12}px`;
          mist.style.top = `${opponentY - 110 - Math.random() * 20}px`;
          mist.style.animationDelay = `${i * 40}ms`;
          container.appendChild(mist);
          elements.push(mist);
        }, i * 60);
        elements.push({ timer: lanceTimer, type: 'timer' });
      }

      // Snowflake particles around formation area
      for (let i = 0; i < 10; i++) {
        const snow = document.createElement('div');
        snow.className = 'glacial-lance-snowflake';
        snow.style.left = `${opponentX + (Math.random() - 0.5) * 120 - 4}px`;
        snow.style.top = `${opponentY - 100 + (Math.random() - 0.5) * 40 - 4}px`;
        snow.style.animationDelay = `${i * 30}ms`;
        container.appendChild(snow);
        elements.push(snow);
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Lance Strike Impact (900-1500ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('glacial-lance-opponent-hit');

      // Lances striking down
      for (let i = 0; i < 5; i++) {
        const strikeTimer = setTimeout(() => {
          const strike = document.createElement('div');
          strike.className = 'glacial-lance-strike';
          strike.style.left = `${opponentX + (i - 2) * 30 - 6}px`;
          strike.style.top = `${opponentY - 30}px`;
          container.appendChild(strike);
          elements.push(strike);
        }, i * 60);
        elements.push({ timer: strikeTimer, type: 'timer' });
      }

      // Ice explosion at impact
      const iceExplosion = document.createElement('div');
      iceExplosion.className = 'glacial-lance-explosion';
      iceExplosion.style.left = `${opponentX - 100}px`;
      iceExplosion.style.top = `${opponentY - 100}px`;
      container.appendChild(iceExplosion);
      elements.push(iceExplosion);

      // Ice shard burst
      for (let i = 0; i < 20; i++) {
        const shardAngle = (i / 20) * 360;
        const shardDist = 60 + Math.random() * 50;
        const shard = document.createElement('div');
        shard.className = 'glacial-lance-shard';
        shard.style.left = `${opponentX - 4}px`;
        shard.style.top = `${opponentY - 4}px`;
        shard.style.setProperty('--scatter-x', `${Math.cos(shardAngle * Math.PI / 180) * shardDist}px`);
        shard.style.setProperty('--scatter-y', `${Math.sin(shardAngle * Math.PI / 180) * shardDist}px`);
        shard.style.animationDelay = `${i * 15}ms`;
        container.appendChild(shard);
        elements.push(shard);
      }

      // Frost shockwaves
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'glacial-lance-shockwave';
          sw.style.left = `${opponentX - 70}px`;
          sw.style.top = `${opponentY - 70}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }
    }, 900);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Frost Aftermath (1500-1900ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('glacial-lance-user-charge');

      // Frost ground effect
      const frostGround = document.createElement('div');
      frostGround.className = 'glacial-lance-frost-ground';
      frostGround.style.left = `${opponentX - 80}px`;
      frostGround.style.top = `${opponentY + 20}px`;
      container.appendChild(frostGround);
      elements.push(frostGround);

      // Lingering frost particles
      for (let i = 0; i < 8; i++) {
        const frost = document.createElement('div');
        frost.className = 'glacial-lance-frost-linger';
        frost.style.left = `${opponentX + (Math.random() - 0.5) * 100 - 6}px`;
        frost.style.top = `${opponentY + (Math.random() - 0.5) * 60 - 6}px`;
        frost.style.animationDelay = `${i * 40}ms`;
        container.appendChild(frost);
        elements.push(frost);
      }
    }, 1500);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1900ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('glacial-lance-user-charge');
      opponentEl.classList.remove('glacial-lance-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1900);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('glacial-lance-user-charge');
      opponentEl.classList.remove('glacial-lance-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

GlacialLanceAnimation.moveType = 'ice';
